import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import i18n from 'i18n-react';
import { withRouter } from 'react-router-dom';
import { HashLink as Link } from 'react-router-hash-link';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Progress from 'components/Progress';

import APIHandler from 'config/APIHandler';

import { useSettings } from 'hooks/useSettings';
import { useUserInfo } from 'hooks/useUserInfo';

const Overlay = () => (
  <div className="overlay">
    <FontAwesomeIcon icon={['fas', 'lock']} />
  </div>
);

// Component that links to a specific training chapter
const TrainingCard = ({
  appId,
  chapter,
  redirPath = '/main/training/chapter/',
  locked = false,
  comingSoon = false,
  // lockedSubtitle = false,
  // lockedSubtitleOverride = null,
}) => {
  // setLoading is passed to image so that it is considered ' loaded'
  const [loading, setLoading] = useState(false);
  const { lang } = useSettings();
  const { user: { email } } = useUserInfo();

  const { langFile } = lang;
  const locale = `${langFile.toLowerCase()}-${langFile
    .replace('pt', 'BR')
    .replace('es', '41')
    .replace('ko', 'kr')
    .toUpperCase()}`;

  // TODO: Check if durationInMinutes is correctly infered. If so, remove todo. Else, fix it.
  const {
    id,
    percentage = 0,
    title,
    chapterItemCountMin: durationInMinutes,
    progress,
    // chapterNumber,
    introChapter,
    essentialsChapter
  } = chapter;

  let translatedTitle = title;
  if (langFile !== 'en' && chapter?.translations?.length > 0) {
    translatedTitle = chapter.translations.find((t) => t.locale === locale)?.title || title;
  }

  // ImgIx compression
  let { image } = chapter;
  image = image.replace('s3.amazonaws.com/developertool', 'travprocdn.imgix.net');
  image = `${image}?auto=format&fit=crop&w=500`;

  const cardClasses = clsx('trainingCard', {
    disabled: locked || comingSoon,
    loading: !!loading, // replace with BEM complient versions.
    locked: !!locked || !!introChapter, // replace with BEM complient versions.
  });

  const handleLoaded = useCallback(() => {
    setLoading(false);
  }, []);

  const buttonText = useMemo(() => {
    let txt = '';
    switch (percentage) {
      case 100:
        txt = i18n.translate('trainingScreens.review');
        // history push
        break;
      case 0:
        txt = i18n.translate('trainingScreens.start');
        break;
      default:
        txt = i18n.translate('trainingScreens.continue');
    }
    return txt;
  }, [percentage]);

  const linkUrl = useMemo(() => {
    const separator = redirPath.endsWith('/') ? '' : '/';

    if (percentage >= 0 && percentage < 100) {
      return `${redirPath}${separator}${id}/${progress}`;
    }
    return `/main/retain/chapter/${id}/0`;
  }, [percentage, redirPath, id, progress]);

  return (
    <Link
      to={linkUrl}
      className={cardClasses}
      {...(locked
        ? {
          onClick: (e) => {
            e.preventDefault();
          },
        } : {}
      )}
      {...(essentialsChapter && percentage >= 100
        ? {
          onClick: () => {
            APIHandler.trackEvent(appId, email, 'retain', 'foundation', id, title).then(() => {});
          }
        } : {}
      )}
    >
      <div className="textContainer">
        {!loading && (
          <div>
            {comingSoon && (
              <h4 className="trainingCard__title">{i18n.translate('trainingScreens.comingSoon', { title })}</h4>
            )}
            {!comingSoon && <h4 className="trainingCard__title">{translatedTitle}</h4>}
            {!locked && (
              <p>
                <FontAwesomeIcon icon={['far', 'clock']} />{' '}
                {i18n.translate('trainingScreens.durationInMinutes', { number: durationInMinutes })}
              </p>
            )}
            {!locked && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {buttonText}
              </button>
            )}
            {locked && <p>{i18n.translate('trainingScreens.unlockDefault')}</p>}
          </div>
        )}
      </div>
      <div className="imageContainer">
        {/* TODO: Refactor Image component before implementation at later date */}
        {/* <Image
          appId={appId}
          load={() => {
            setLoading(false);
          }}
          src={image}
          alt={title}
          gradient
          staticImage
        /> */}
        <img src={image} alt={title} onLoad={handleLoaded} />
        {!loading && locked && <Overlay />}
        {/* {!loading && <span className="chapterNumber">{`0${index + 1}`.slice(-2)}</span> && (
          <span className="chapterComplete">
            {percentage === 100 && !locked && (
              <FontAwesomeIcon icon={['far', 'check-cercle']} className="chapterComplete" />
            )}
          </span>
        )} */}
      </div>
      {!locked && !loading && <Progress id={`ch${id}`} value={percentage} />}
    </Link>
  );
};
export default withRouter(TrainingCard);

TrainingCard.propTypes = {
  appId: PropTypes.number.isRequired,
  chapter: PropTypes.shape({
    chapterItemCountMin: PropTypes.number.isRequired,
    id: PropTypes.number.isRequired,
    image: PropTypes.string.isRequired,
    percentage: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    progress: PropTypes.number,
    translations: PropTypes.arrayOf(
      PropTypes.shape({
        locale: PropTypes.string,
        title: PropTypes.string,
      }),
    ),
    // chapterNumber: PropTypes.number,
    introChapter: PropTypes.bool,
  }).isRequired,
  redirPath: PropTypes.string,
  locked: PropTypes.bool,
  comingSoon: PropTypes.bool,
  lockedSubtitle: PropTypes.bool,
  lockedSubtitleOverride: PropTypes.string,
};
